/**
 * Utility for scrolling to inline citations from the References section.
 * EVI-385: Enables reference → citation linking.
 */

import { CITATION_HIGHLIGHT_EVENT, CITATION_OPEN_EVENT } from './CitationBadge';
import { DEFAULT_REFERENCE_SURFACE, type ReferenceSurface } from './referenceSurface';

/** Delay before opening popover (allows scroll to complete) */
const OPEN_POPOVER_DELAY_MS = 300;

/**
 * Scrolls to the matching inline citation.
 * `evidence` opens its popover. `work` marks the badge instead.
 */
const pickCitationElement = (
  matches: NodeListOf<HTMLElement>,
  runIds: readonly string[] | undefined
): HTMLElement | undefined => {
  if (!runIds || runIds.length === 0) return matches[0];

  const allowed = new Set(runIds);
  return Array.from(matches).find((element) => {
    const runId = element.getAttribute('data-citation-run');
    return runId !== null && allowed.has(runId);
  });
};

export const scrollToInlineCitation = (
  url: string,
  surface: ReferenceSurface = DEFAULT_REFERENCE_SURFACE,
  runIds?: readonly string[]
): boolean => {
  // A Work list names its runs so a shared source stays in that answer.
  // Standalone Evidence leaves runIds empty and keeps the first badge.
  const citationElement = pickCitationElement(
    document.querySelectorAll<HTMLElement>(`[data-citation-url="${CSS.escape(url)}"]`),
    runIds
  );

  if (!citationElement) {
    return false;
  }

  if (surface === 'work') {
    citationElement.dispatchEvent(new CustomEvent(CITATION_HIGHLIGHT_EVENT));
  }

  // Scroll the element into view with smooth animation
  citationElement.scrollIntoView({
    behavior: 'smooth',
    block: 'center',
    inline: 'nearest',
  });

  if (surface === 'evidence') {
    // After scroll completes, dispatch event to open the popover
    setTimeout(() => {
      citationElement.dispatchEvent(new CustomEvent(CITATION_OPEN_EVENT));
    }, OPEN_POPOVER_DELAY_MS);
  }

  return true;
};
